import {
  addToast,
  showDialogConfirm,
  TableCollapsable,
  TablePagination,
} from '@octano/global-ui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useActionKitImp } from '../../../../../components/ozmoit/common';
import TableEmptyContent from '../../../../../components/text/TableEmptyContent';
import { PathsLayouts } from '../../../../../config/routes';
import { TranslationsKeys } from '../../../../../locales/translations';
import { deleteStudyPlanAction } from '../../actions';
import {
  NonStructuredType,
  StudyPlanVersionStatus,
  updateVersionStatusRequest,
} from '../../api';
import {
  useStudyPlansListLoaderData,
  useStudyPlansListLoader,
} from '../../hooks/StudyPlansListLoader';
import { useStudyPlansColumns } from '../../hooks/useStudyPlansColumns';
import { StudyPlanDetail } from '../StudyPlanDetail';

const ITEMS_PER_PAGE = 10;

export function StudyPlansList() {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_STUDY_PLANS,
  );
  const prefix = 'list';
  const history = useHistory();

  const { data, total, total_pages } = useStudyPlansListLoaderData();
  const { query, updateQuery, refresh } = useStudyPlansListLoader();

  const handleStatusChange = useCallback(
    async (versionId: number, newStatus: StudyPlanVersionStatus) => {
      const { error } = await updateVersionStatusRequest(versionId, newStatus);
      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`common.msg.statusChangeError`),
          className: 'long-toast',
        });
        return;
      }
      updateQuery({ ...query });
    },
    [t, query, updateQuery],
  );

  const goToManage = useCallback(
    (id: number, type: string | undefined) => {
      const path =
        type === NonStructuredType.COURSE
          ? `${PathsLayouts.nonStructured}/study-plans/course/${id}`
          : `${PathsLayouts.nonStructured}/study-plans/program-course/${id}`;
      history.push(path);
    },
    [history],
  );

  const { action: deleteStudyPlan } = useActionKitImp(deleteStudyPlanAction, {
    onSuccess: (_data, params) => {
      const successKey =
        params.type === NonStructuredType.COURSE
          ? `${prefix}.delete.successCourse`
          : `${prefix}.delete.successProgram`;
      addToast({
        icon: 'success',
        color: 'success',
        text: t(successKey),
      });
      refresh();
    },
    onError: {
      unknown: () => {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${prefix}.delete.error`),
        });
      },
    },
  });

  const handleDelete = useCallback(
    (id: number, type: string | undefined) => {
      const isCourse = type === NonStructuredType.COURSE;
      showDialogConfirm({
        icon: { name: 'warning', color: 'primary' },
        title: t(
          isCourse
            ? `${prefix}.delete.titleCourse`
            : `${prefix}.delete.titleProgram`,
        ),
        subtitle: t(
          isCourse
            ? `${prefix}.delete.descriptionCourse`
            : `${prefix}.delete.descriptionProgram`,
        ),
        btnConfirm: {
          text: t(`${prefix}.delete.confirm`),
          onConfirm: () => {
            deleteStudyPlan({ id, type: type as NonStructuredType });
          },
        },
        btnClose: { text: t(`${prefix}.delete.cancel`) },
      });
    },
    [t, deleteStudyPlan],
  );

  const columns = useStudyPlansColumns({
    onStatusChange: handleStatusChange,
    onManage: goToManage,
    onDelete: handleDelete,
  });

  return (
    <div id={'curriculum-mention-list-table'}>
      <TableCollapsable
        columns={columns}
        data={data}
        striped
        isStartCollapsed={() => false}
        noResultsText={
          <TableEmptyContent
            title={t(`${prefix}.emptyTitle`)}
            subtitle={t(`${prefix}.emptySubtitle`)}
          />
        }
        secondColumn={{
          columnName: 'detail',
          headerText: t(`${prefix}.detail`),
          cellFormat: ({ row }) => <StudyPlanDetail studyPlan={row} />,
        }}
      />
      {total > ITEMS_PER_PAGE && (
        <TablePagination
          pagination={{
            currentPage: query.page + 1,
            totalItems: total,
            totalPages: total_pages,
            itemsPerPage: ITEMS_PER_PAGE,
            onChangePage: (page: number) =>
              updateQuery({ ...query, page: page - 1 }),
          }}
        />
      )}
    </div>
  );
}
